import type { ComponentType } from "react";

import type { MenuItemOption } from "app/pages/sales-intelligence/common-components/options-menu/types";

import { useCrmEnrichmentContext } from "../contexts/CrmEnrichmentContext";
import crmEnrichmentTrackingService from "../services/tracking/trackingService";
import type { CrmEnrichmentJob } from "../types/types";

type TrackableProps = {
    job: CrmEnrichmentJob;
    onToggle: (jobId: string, value: boolean) => void;
    onToggleMenu?: (isOpen: boolean, jobName: string) => void;
    onSelectOption?: (option: MenuItemOption) => void;
};

export function withCrmEnrichmentJobTracking<P extends TrackableProps>(
    WrappedComponent: ComponentType<P>,
): ComponentType<P> {
    return function WrappedWithJobToggleTracking(props: P) {
        const { crmProvider } = useCrmEnrichmentContext();

        const wrappedOnToggle = (jobId: string, value: boolean) => {
            crmEnrichmentTrackingService.trackToggleJobClick(value, props.job, crmProvider);

            props.onToggle(jobId, value);
        };

        const wrappedOnToggleMenu = (isOpen: boolean, jobName: string) => {
            if (isOpen) {
                crmEnrichmentTrackingService.trackOpenJobOptions(jobName, crmProvider);
            }

            props.onToggleMenu?.(isOpen, jobName);
        };

        const wrappedOnSelectOption = (option: MenuItemOption) => {
            crmEnrichmentTrackingService.trackSelectJobOption(option, crmProvider);

            props.onSelectOption?.(option);
        };

        return (
            <WrappedComponent
                {...props}
                onToggle={wrappedOnToggle}
                onToggleMenu={wrappedOnToggleMenu}
                onSelectOption={wrappedOnSelectOption}
            />
        );
    };
}
